import type {Evidence} from '@/hooks/sgsst/evidencias/use-evidence'
import type {Employee} from '@/hooks/empresa/identidad/use-employees'
import type {SgsstItem, SoporteCasilla} from '@/hooks/sgsst/evaluacion/use-sgsst'

export type SoporteDesignation = {
  standardCode: string
  hasCarta: boolean
  expired: boolean
}

export type Readiness = {
  covered: number
  total: number
  sinSoporte: string[]
  pendingVigencia: string[]
}

/** The API returns only the pinned chapter's items, so applicability is membership here. */
export function personScopedApplicable(items: SgsstItem[]): SgsstItem[] {
  return items.filter((item) => item.person_scoped && item.status !== 'no_aplica')
}

export function groupEvidenceByStandard(evidence: Evidence[]): Map<string, Evidence[]> {
  const byStandard = new Map<string, Evidence[]>()
  for (const item of evidence) {
    if (!byStandard.has(item.standard_code)) byStandard.set(item.standard_code, [])
    byStandard.get(item.standard_code)!.push(item)
  }
  return byStandard
}

/** For 3.2.x standards the employer's live «sin eventos» declaration is the soporte. */
export function soporte(
  item: SgsstItem,
  filesByStandard: Map<string, Evidence[]>,
  designations: SoporteDesignation[] = []
): boolean {
  if ((filesByStandard.get(item.code) ?? []).length > 0) return true
  if (item.sin_eventos) return true
  return designations.some((d) => d.standardCode === item.code && d.hasCarta && !d.expired)
}

/** A casilla that admits «sin eventos» is covered while the declaration stands (KAI-143). */
export function soportesIncompletos(item: SgsstItem): boolean {
  if (item.status !== 'cumple') return false
  return item.soportes.some(
    (casilla) =>
      (casilla.estado === 'vencido' || casilla.estado === 'falta') && !(item.sin_eventos && casilla.admite_sin_eventos)
  )
}

/** Over the assessment's own items, never the raw evidence list, so orphaned files can't count.
 * pendingVigencia is a task list: it counts regardless of the mark. */
export function readiness(items: SgsstItem[], files: Evidence[], designations: SoporteDesignation[] = []): Readiness {
  const byStandard = groupEvidenceByStandard(files)
  const cumple = items.filter((item) => item.status === 'cumple')
  const sinSoporte = cumple.filter((item) => !soporte(item, byStandard, designations)).map((item) => item.code)
  const pendingVigencia = items
    .filter((item) => (byStandard.get(item.code) ?? []).some((file) => file.pending_confirmation))
    .map((item) => item.code)
  return {covered: cumple.length - sinSoporte.length, total: cumple.length, sinSoporte, pendingVigencia}
}

export function coveredSet(standardCode: string, evidence: Evidence[]): Set<string> {
  const ids = new Set<string>()
  for (const item of evidence) {
    if (item.standard_code !== standardCode) continue
    for (const employee of item.employees) ids.add(employee.id)
  }
  return ids
}

export function perPerson(
  employeeId: string,
  items: SgsstItem[],
  evidence: Evidence[]
): {covered: number; total: number; gaps: SgsstItem[]} {
  const applicable = personScopedApplicable(items)
  const gaps = applicable.filter((item) => !coveredSet(item.code, evidence).has(employeeId))
  return {covered: applicable.length - gaps.length, total: applicable.length, gaps}
}

export function uncoveredActives(standardCode: string, evidence: Evidence[], roster: Employee[]): Employee[] {
  const covered = coveredSet(standardCode, evidence)
  return roster.filter((employee) => employee.status === 'active' && !covered.has(employee.id))
}

/** A contratista aports their own document where the casilla declares `fuente_contratista`. */
export function efectivaFuente(
  casilla: SoporteCasilla,
  employmentType?: Employee['employment_type']
): SoporteCasilla['fuente'] {
  if (employmentType === 'contratista' && casilla.fuente_contratista) return casilla.fuente_contratista
  return casilla.fuente
}

/** An employee cannot aportar a `fuente=empresa` document, except the médico one: the person usually
 * keeps a copy of their concepto de aptitud. */
function esSolicitable(casilla: SoporteCasilla, employmentType?: Employee['employment_type']): boolean {
  const fuente = efectivaFuente(casilla, employmentType)
  if (fuente === 'empleado') return true
  if (fuente === 'empresa') return casilla.medico
  return false
}

export function tieneCasillaSolicitable(soportes: SoporteCasilla[]): boolean {
  return soportes.some((casilla) => esSolicitable(casilla))
}

/** `falta` is neutral, not a false alarm. */
export function soporteEstadoTone(
  estado: 'falta' | 'cargado' | 'vence_pronto' | 'vencido' | 'no_aplica'
): 'neutral' | 'green' | 'amber' | 'red' {
  if (estado === 'vencido') return 'red'
  if (estado === 'vence_pronto') return 'amber'
  if (estado === 'cargado') return 'green'
  return 'neutral'
}
